import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { ENV } from "../../config/env";

const supportApi = axios.create({
  baseURL: ENV.API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const ISSUE_OPTIONS = [
  { label: "Refund", value: "REFUND" },
  { label: "Rescheduling", value: "RESCHEDULING" },
  { label: "Cancellation", value: "CANCELLATION" },
];

const RESCHEDULE_REASON_OPTIONS = [
  "Personal Emergency",
  "Work / Business",
  "Medical Reason",
  "Other",
];

const RESCHEDULE_TIME_OPTIONS = [
  "Any",
  "Morning (6AM-12PM)",
  "Afternoon (12PM-6PM)",
  "Evening (6PM-12AM)",
  "Night (12AM-6AM)",
];

/** Used when flows skip the manual details step (from PNR / passenger / user record). */
const DESCRIPTION_FROM_BOOKING =
  "Contact details verified from booking (PNR lookup). No additional message provided.";

function formatTravelDate(val) {
  if (val == null || val === "") return "N/A";
  try {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  } catch {
    return "N/A";
  }
}

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function passengerRowLabel(p) {
  const name = p?.name != null && String(p.name).trim() !== "" ? String(p.name).trim() : "—";
  const t = p?.pax_type_label != null && String(p.pax_type_label).trim() !== ""
    ? String(p.pax_type_label).trim()
    : "—";
  return `${name} (${t})`;
}

export default function SupportPage() {
  const bottomRef = useRef(null);

  const [messages, setMessages] = useState(() => [
    {
      id: "welcome",
      type: "bot",
      text:
        "Hi! I'm here to help you with your flight booking. What do you need help with?",
    },
  ]);
  const [step, setStep] = useState(1);
  const [issueType, setIssueType] = useState("");
  const [pnr, setPnr] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pnrInput, setPnrInput] = useState("");

  const [cancellationType, setCancellationType] = useState("");
  const [selectedPaxForCancel, setSelectedPaxForCancel] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [bookingPassengers, setBookingPassengers] = useState([]);

  const [rescheduleReason, setRescheduleReason] = useState("");
  const [rescheduleNewDate, setRescheduleNewDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("Any");
  const [rescheduleAirline, setRescheduleAirline] = useState("");
  const [selectedReschedulePax, setSelectedReschedulePax] = useState([]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const pushMessage = useCallback((msg) => {
    setMessages((m) => [...m, { ...msg, id: msg.id ?? uid() }]);
  }, []);

  const resetFlow = useCallback(() => {
    setStep(1);
    setIssueType("");
    setPnr("");
    setBookingData(null);
    setPnrInput("");
    setLoading(false);
    setCancellationType("");
    setSelectedPaxForCancel([]);
    setSelectedTrip(null);
    setBookingPassengers([]);
    setRescheduleReason("");
    setRescheduleNewDate("");
    setRescheduleTime("Any");
    setRescheduleAirline("");
    setSelectedReschedulePax([]);
    setMessages([
      {
        id: "welcome",
        type: "bot",
        text:
          "Hi! I'm here to help you with your flight booking. What do you need help with?",
      },
    ]);
  }, []);

  const onSelectIssue = (opt) => {
    pushMessage({ type: "user", text: opt.label });
    setIssueType(opt.value);
    pushMessage({
      type: "bot",
      text: "Please enter your PNR/Booking Reference number:",
    });
    setStep(2);
    setPnrInput("");
  };

  const onSubmitPnr = async () => {
    const trimmed = pnrInput.trim();
    if (!trimmed) return;
    pushMessage({ type: "user", text: trimmed });
    setPnr(trimmed);
    setPnrInput("");
    setLoading(true);
    pushMessage({
      type: "bot",
      text: "Looking up your booking...",
      isTyping: true,
    });

    try {
      const res = await supportApi.post("/support/validate-pnr", {
        pnr: trimmed,
      });
      const data = res.data;
      setMessages((m) => m.filter((x) => !x.isTyping));

      if (!data.success || !data.data) {
        const msg =
          data.message || "PNR not found. Please check and try again.";
        pushMessage({ type: "bot", text: msg });
        setStep(2);
        setPnr("");
        setBookingData(null);
        return;
      }

      const b = data.data;
      setBookingData(b);
      const names = (b.passengers || [])
        .map((p) => p.name)
        .filter(Boolean)
        .join("\n");
      const detailText =
        `Found your booking:\n` +
        `✈ ${b.origin ?? "—"} → ${b.destination ?? "—"}\n` +
        `📅 Travel Date: ${formatTravelDate(b.travel_date)}\n` +
        `👥 Passengers: ${b.passenger_count ?? 0}` +
        (names ? `\n${names}` : "") +
        `\n\nIs this your booking?`;
      pushMessage({ type: "bot", text: detailText });
      setStep(3);
    } catch (e) {
      setMessages((m) => m.filter((x) => !x.isTyping));
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Something went wrong. Please try again.";
      pushMessage({ type: "bot", text: msg });
      setStep(2);
      setPnr("");
      setBookingData(null);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmBooking = (yes) => {
    if (yes) {
      pushMessage({ type: "user", text: "Yes, Continue" });
      if (issueType === "CANCELLATION") {
        setCancellationType("");
        setSelectedPaxForCancel([]);
        setBookingPassengers([]);
        setSelectedTrip(null);
        if (bookingData?.is_return_ticket === true) {
          pushMessage({
            type: "bot",
            text: "Your booking includes multiple trips. Which trip would you like to cancel?",
          });
          setStep(34);
        } else {
          pushMessage({
            type: "bot",
            text: "What type of cancellation do you need?",
          });
          setStep(35);
        }
        return;
      } else if (issueType === "RESCHEDULING") {
        pushMessage({
          type: "bot",
          text: "Why do you need to reschedule your flight?",
        });
        setRescheduleReason("");
        setRescheduleNewDate("");
        setRescheduleTime("Any");
        setRescheduleAirline("");
        setSelectedReschedulePax([]);
        setBookingPassengers([]);
        setStep(36);
      } else {
        void submitRefundFromBooking();
      }
    } else {
      pushMessage({ type: "user", text: "No, Try Again" });
      pushMessage({
        type: "bot",
        text: "Please enter your PNR/Booking Reference number:",
      });
      setBookingData(null);
      setPnr("");
      setCancellationType("");
      setSelectedPaxForCancel([]);
      setSelectedTrip(null);
      setBookingPassengers([]);
      setRescheduleReason("");
      setRescheduleNewDate("");
      setRescheduleTime("Any");
      setRescheduleAirline("");
      setSelectedReschedulePax([]);
      setStep(2);
    }
  };

  const onSelectReturnTripLeg = (trip) => {
    pushMessage({ type: "user", text: `✈ ${trip.label}` });
    setSelectedTrip(trip);
    pushMessage({
      type: "bot",
      text: "What type of cancellation do you need?",
    });
    setStep(35);
  };

  const onSelectBothReturnTrips = () => {
    pushMessage({ type: "user", text: "Both Trips" });
    setSelectedTrip("both");
    pushMessage({
      type: "bot",
      text: "What type of cancellation do you need?",
    });
    setStep(35);
  };

  const getBookingContact = () => ({
    email: String(bookingData?.customer_email || "").trim(),
    name: String(
      bookingData?.customer_name ||
        bookingData?.passengers?.[0]?.name ||
        ""
    ).trim(),
    phone: String(bookingData?.customer_phone || "").trim(),
  });

  const completeRaiseComplaint = async (descriptionToSend, contact) => {
    const emailTrim = (contact.email || "").trim();
    const nameTrim = (contact.name || "").trim() || "Customer";
    const phoneTrim = (contact.phone || "").trim();

    if (!emailTrim) {
      toast.error("No email on file for this booking.");
      pushMessage({
        type: "bot",
        text:
          "We couldn’t find an email address linked to this booking (from your account or passenger records). Please contact support directly for help.",
      });
      return;
    }

    setLoading(true);
    pushMessage({
      type: "bot",
      text: "Submitting your request...",
      isTyping: true,
    });

    try {
      const res = await supportApi.post("/support/raise-complaint", {
        pnr,
        booking_id: bookingData?.booking_id,
        issue_type: issueType,
        description: descriptionToSend,
        customer_name: nameTrim,
        customer_email: emailTrim,
        customer_phone: phoneTrim || undefined,
      });
      setMessages((m) => m.filter((x) => !x.isTyping));

      const payload = res.data;
      if (!payload.success || !payload.data) {
        const errMsg =
          payload.message || "Failed to submit. Please try again.";
        pushMessage({ type: "bot", text: `❌ ${errMsg}` });
        return;
      }

      const { ticket_id, zoho_ticket_id, message } = payload.data;
      const ref = zoho_ticket_id || ticket_id;
      pushMessage({
        type: "bot",
        text:
          `✅ ${message || "Your ticket has been successfully raised. One of our agents will review it and get back to you shortly."}\n\n` +
          `Ticket ID: #${ticket_id}\n` +
          `Our support team will contact you shortly.\n` +
          `Reference: ${ref ?? ticket_id}`,
      });
      setCancellationType("");
      setSelectedPaxForCancel([]);
      setBookingPassengers([]);
      setRescheduleReason("");
      setRescheduleNewDate("");
      setRescheduleTime("Any");
      setRescheduleAirline("");
      setSelectedReschedulePax([]);
      setStep(5);
    } catch (e) {
      setMessages((m) => m.filter((x) => !x.isTyping));
      const errMsg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to submit. Please try again.";
      pushMessage({
        type: "bot",
        text: `❌ ${errMsg}\n\nPlease try again or contact support.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const submitRefundFromBooking = async () => {
    await completeRaiseComplaint(DESCRIPTION_FROM_BOOKING, getBookingContact());
  };

  const submitCancellationOrReschedule = async (opts = {}) => {
    const cancelTypeEff = opts.cancellationType ?? cancellationType;
    const paxList = opts.selectedPaxForCancel ?? selectedPaxForCancel;

    const { name, email, phone } = getBookingContact();

    let descriptionToSend = DESCRIPTION_FROM_BOOKING;
    if (issueType === "CANCELLATION") {
      const header =
        cancelTypeEff === "partial"
          ? "PARTIAL CANCELLATION"
          : "FULL CANCELLATION";
      const paxLine =
        cancelTypeEff === "partial" && paxList.length > 0
          ? `Passengers to cancel: ${paxList.join(", ")}\n`
          : "";
      const descTrimmed = DESCRIPTION_FROM_BOOKING;
      const tripLine =
        selectedTrip === "both"
          ? "Both trips (Full return cancellation)"
          : selectedTrip?.label || "Single trip";
      descriptionToSend = `[${header}]
Trip: ${tripLine}
${paxLine}Customer description: ${descTrimmed}`;
    } else if (issueType === "RESCHEDULING") {
      descriptionToSend = `[RESCHEDULING REQUEST]
Reason: ${rescheduleReason}
Preferred Travel Date: ${rescheduleNewDate}
Preferred Time: ${rescheduleTime}
Preferred Airline: ${rescheduleAirline.trim() || "No preference"}
Customer description: ${DESCRIPTION_FROM_BOOKING}`;
    }

    await completeRaiseComplaint(descriptionToSend, { name, email, phone });
  };

  const onSelectFullCancellation = () => {
    setCancellationType("full");
    pushMessage({ type: "user", text: "Full Cancellation" });
    setSelectedPaxForCancel([]);
    setBookingPassengers([]);
    void submitCancellationOrReschedule({
      cancellationType: "full",
      selectedPaxForCancel: [],
    });
  };

  const onSelectPartialCancellation = () => {
    setCancellationType("partial");
    pushMessage({ type: "user", text: "Partial Cancellation" });
    const list =
      selectedTrip && selectedTrip !== "both"
        ? Array.isArray(selectedTrip.passengers)
          ? selectedTrip.passengers
          : []
        : Array.isArray(bookingData?.passengers)
          ? bookingData.passengers
          : [];
    setBookingPassengers(list);
    setSelectedPaxForCancel([]);
    pushMessage({
      type: "bot",
      text:
        "Which passengers do you want to cancel? Please select from the list below:",
    });
    setStep(39);
  };

  const togglePaxForCancel = (label) => {
    setSelectedPaxForCancel((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  const onPassengerSelectionContinue = () => {
    if (selectedPaxForCancel.length === 0) return;
    pushMessage({
      type: "user",
      text: selectedPaxForCancel.join(", "),
    });
    void submitCancellationOrReschedule({
      cancellationType: "partial",
      selectedPaxForCancel: [...selectedPaxForCancel],
    });
  };

  const onSelectRescheduleReason = (reason) => {
    setRescheduleReason(reason);
    pushMessage({ type: "user", text: reason });
    pushMessage({
      type: "bot",
      text: "Please provide your preferred travel details:",
    });
    setStep(37);
  };

  const onRescheduleDetailsContinue = () => {
    if (!rescheduleNewDate.trim()) return;
    if (!rescheduleTime) return;
    const summary = `Date: ${rescheduleNewDate}, Time: ${rescheduleTime}${
      rescheduleAirline.trim()
        ? `, Airline: ${rescheduleAirline.trim()}`
        : ""
    }`;
    pushMessage({ type: "user", text: summary });
    void submitCancellationOrReschedule({});
  };

  const toggleReschedulePax = (label) => {
    setSelectedReschedulePax((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  const onSelectAllReschedulePax = () => {
    const labels = bookingPassengers.map((p) => passengerRowLabel(p));
    setSelectedReschedulePax(labels);
  };

  const onReschedulePassengerContinue = () => {
    if (selectedReschedulePax.length === 0) return;
    pushMessage({
      type: "user",
      text: selectedReschedulePax.join(", "),
    });
    void submitCancellationOrReschedule({});
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <style>{`
        @keyframes supportChatIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .support-msg-animate {
          animation: supportChatIn 0.35s ease-out forwards;
        }
      `}</style>

      <header className="shrink-0 border-b bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <span
            className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl"
            aria-hidden
          >
            🤖
          </span>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Navigatio Support</h1>
            <p className="text-xs text-gray-500">We typically reply soon</p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 pb-[min(50vh,420px)] sm:pb-[380px]">
        <div className="mx-auto flex max-w-2xl flex-col gap-3">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`support-msg-animate flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={
                  msg.type === "user"
                    ? "max-w-[85%] rounded-2xl rounded-br-md bg-blue-600 px-4 py-2.5 text-sm text-white shadow-sm"
                    : "max-w-[85%] rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800 shadow-sm whitespace-pre-wrap"
                }
              >
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      <footer className="fixed bottom-0 left-0 right-0 max-h-[min(50vh,420px)] overflow-y-auto border-t bg-white/95 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] backdrop-blur-sm">
        <div className="mx-auto max-w-2xl space-y-3">
          {step === 1 ? (
            <div className="flex flex-wrap gap-2">
              {ISSUE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={loading}
                  onClick={() => onSelectIssue(opt)}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <input
                type="text"
                value={pnrInput}
                onChange={(e) => setPnrInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !loading && onSubmitPnr()}
                placeholder="Enter PNR / booking reference"
                disabled={loading}
                className="min-h-[44px] flex-1 rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
              />
              <button
                type="button"
                disabled={loading || !pnrInput.trim()}
                onClick={onSubmitPnr}
                className="rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Submit
              </button>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={loading}
                onClick={() => onConfirmBooking(true)}
                className="rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Yes, Continue
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => onConfirmBooking(false)}
                className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                No, Try Again
              </button>
            </div>
          ) : null}

          {step === 34 ? (
            <div className="flex flex-wrap gap-2">
              {(bookingData?.tripsWithPassengers ?? []).map((trip) => (
                <button
                  key={trip.booking_id}
                  type="button"
                  disabled={loading}
                  onClick={() => onSelectReturnTripLeg(trip)}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  ✈ {trip.label}
                </button>
              ))}
              <button
                type="button"
                disabled={loading}
                onClick={onSelectBothReturnTrips}
                className="rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Both Trips
              </button>
            </div>
          ) : null}

          {step === 35 ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={loading}
                onClick={onSelectFullCancellation}
                className="rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Full Cancellation
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={onSelectPartialCancellation}
                className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                Partial Cancellation
              </button>
            </div>
          ) : null}

          {step === 36 ? (
            <div className="flex flex-wrap gap-2">
              {RESCHEDULE_REASON_OPTIONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  disabled={loading}
                  onClick={() => onSelectRescheduleReason(reason)}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  {reason}
                </button>
              ))}
            </div>
          ) : null}

          {step === 37 ? (
            <div className="max-h-[min(50vh,320px)] space-y-3 overflow-y-auto pr-1">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">
                  New Travel Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={rescheduleNewDate}
                  onChange={(e) => setRescheduleNewDate(e.target.value)}
                  disabled={loading}
                  className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">
                  Preferred Time <span className="text-red-600">*</span>
                </label>
                <select
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  disabled={loading}
                  className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                >
                  {RESCHEDULE_TIME_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">
                  Preferred Airline (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. IndiGo, Air India (optional)"
                  value={rescheduleAirline}
                  onChange={(e) => setRescheduleAirline(e.target.value)}
                  disabled={loading}
                  className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                />
              </div>
              <button
                type="button"
                disabled={loading || !rescheduleNewDate.trim()}
                onClick={onRescheduleDetailsContinue}
                className="w-full rounded-xl bg-black py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          ) : null}

          {step === 39 ? (
            <div className="space-y-3">
              <div className="max-h-[min(36vh,260px)] space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-3">
                {bookingPassengers.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    No passenger list available. Go back and choose full
                    cancellation, or re-validate your PNR.
                  </p>
                ) : (
                  bookingPassengers.map((p, idx) => {
                    const label = passengerRowLabel(p);
                    const checked = selectedPaxForCancel.includes(label);
                    return (
                      <label
                        key={`${label}-${idx}`}
                        className="flex cursor-pointer items-start gap-3 rounded-lg border border-transparent bg-white px-3 py-2 hover:border-gray-200"
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300"
                          checked={checked}
                          onChange={() => togglePaxForCancel(label)}
                        />
                        <span className="text-sm text-gray-800">{label}</span>
                      </label>
                    );
                  })
                )}
              </div>
              <button
                type="button"
                disabled={
                  loading ||
                  bookingPassengers.length === 0 ||
                  selectedPaxForCancel.length === 0
                }
                onClick={onPassengerSelectionContinue}
                className="w-full rounded-xl bg-black py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          ) : null}

          {step === 5 ? (
            <button
              type="button"
              onClick={resetFlow}
              className="w-full rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              Raise Another Ticket or Request
            </button>
          ) : null}
        </div>
      </footer>
    </div>
  );
}
